//@ts-nocheck
// GCC COMPATIBILITY - Simplified 2 Page Flow with API Integration
import React, { useState } from "react";
import {
  ChevronLeft,
  Heart,
  Clock,
  MessageCircle,
  Star,
  Sun,
  Zap,
} from "lucide-react";
import { useChat } from "../../contexts/ChatContext";
import { ls, useLnLanguage } from "../../utils/lnText";

// ─── Types ────────────────────────────────────────────────────────────────────
interface GCC3BoxData {
  energy_signature: "Soft" | "Balanced" | "Deep" | null;
  dob: string | null;
  destiny_time: string | null;
  birth_time: string | null;
  birth_city: string | null;
}

interface GCCCompatibilityResponse {
  pages?: {
    pageId: string;
    title: string;
    components?: {
      scoreGauge?: { value: number; label: string };
      lifeGraph?: { categories: string[]; value: number[] };
      summary?: { type: string; title: string; text: string }[];
    };
    cards?: { id: string; title: string; icon: string; description: string }[];
  }[];
}

// ─── Colors ───────────────────────────────────────────────────────────────
const C = {
  purple: "#6D4C92",
  purpleSoft: "rgba(109,76,146,0.12)",
  purpleBorder: "rgba(109,76,146,0.4)",
  white: "#fff",
  lightBg: "#F8F4FF",
  lightCard: "#fff",
  lightBorder: "#E8E4DE",
  textDark: "#3D3A35",
  textMuted: "#9A9590",
};

// ─── Options ───────────────────────────────────────────────────────────────
const ENERGY_OPTIONS = ["Soft", "Balanced", "Deep"];
const YEARS = Array.from({ length: 100 }, (_, i) => ({
  value: String(new Date().getFullYear() - i),
  label: String(new Date().getFullYear() - i),
}));
const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: String(i + 1),
}));
const DAYS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: String(i + 1),
}));
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i).padStart(2, "0"),
  label: `${String(i).padStart(2, "0")}:00`,
}));

// ─── ICONS ───────────────────────────────────────────────────────────────
const CARD_ICONS: Record<string, React.ReactNode> = {
  heart: <Heart size={20} />,
  clock: <Clock size={20} />,
  wave: <MessageCircle size={20} />,
  star: <Star size={20} />,
  sun: <Sun size={20} />,
  message: <MessageCircle size={20} />,
  growth: <Zap size={20} />,
};

// ─── Main Component ────────────────────────────────────────────────────────────
interface GCC3BoxProps {
  onBack: () => void;
  categoryId: string;
  subCategoryId: string;
  userId: string;
  onSubmit: (selfData: GCC3BoxData, partnerData: GCC3BoxData) => void;
}

const GCCCompatibility3Box: React.FC<GCC3BoxProps> = ({
  onBack,
  categoryId,
  subCategoryId,
  userId,
  onSubmit,
}) => {
  useLnLanguage();
  // State
  const [page, setPage] = useState<"person1" | "person2">("person1");

  // Person 1 data
  const [person1Energy, setPerson1Energy] = useState("");
  const [person1DobY, setPerson1DobY] = useState("");
  const [person1DobM, setPerson1DobM] = useState("");
  const [person1DobD, setPerson1DobD] = useState("");
  const [person1TimeH, setPerson1TimeH] = useState("");
  const [person1TimeMin, setPerson1TimeMin] = useState("");

  // Person 2 data
  const [person2Energy, setPerson2Energy] = useState("");
  const [person2DobY, setPerson2DobY] = useState("");
  const [person2DobM, setPerson2DobM] = useState("");
  const [person2DobD, setPerson2DobD] = useState("");
  const [person2TimeH, setPerson2TimeH] = useState("");
  const [person2TimeMin, setPerson2TimeMin] = useState("");

  // Computed values
  const person1Dob =
    person1DobY && person1DobM && person1DobD
      ? `${person1DobD}/${person1DobM}/${person1DobY}`
      : null;
  const person1Destiny = person1TimeH
    ? `${person1TimeH}:${person1TimeMin || "00"}`
    : null;
  const person2Dob =
    person2DobY && person2DobM && person2DobD
      ? `${person2DobD}/${person2DobM}/${person2DobY}`
      : null;
  const person2Destiny = person2TimeH
    ? `${person2TimeH}:${person2TimeMin || "00"}`
    : null;

  // Build GCC 3-box data
  const gcc3BoxSelf: GCC3BoxData = {
    energy_signature: (person1Energy as "Soft" | "Balanced" | "Deep") || null,
    dob: person1Dob,
    destiny_time: person1Destiny,
    birth_time: person1Destiny,
    birth_city: null,
  };

  const gcc3BoxPartner: GCC3BoxData = {
    energy_signature: (person2Energy as "Soft" | "Balanced" | "Deep") || null,
    dob: person2Dob,
    destiny_time: person2Destiny,
    birth_time: person2Destiny,
    birth_city: null,
  };

  // Submit handler - passes data to parent
  const handleSubmit = () => {
    if (!person2Dob) return;
    onSubmit(gcc3BoxSelf, gcc3BoxPartner);
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const styles = {
    container: {
      flex: 1,
      overflowY: "auto" as const,
      background: C.lightBg,
      minHeight: "100%",
      display: "flex" as const,
      flexDirection: "column" as const,
    },
    content: {
      padding: "20px 16px 40px",
      maxWidth: 480,
      margin: "0 auto",
      width: "100%" as const,
      display: "flex" as const,
      flexDirection: "column" as const,
      alignItems: "center" as const,
    },
    card: {
      borderRadius: 16,
      background: C.lightCard,
      border: `1px solid ${C.lightBorder}`,
      padding: "20px 16px",
      marginBottom: 16,
      width: "100%",
    },
    icon: {
      width: 64,
      height: 64,
      borderRadius: "50%",
      background: C.purpleSoft,
      border: `2px solid ${C.purpleBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 28,
      margin: "0 auto 16px",
    },
    h1: {
      fontSize: 22,
      fontWeight: 600,
      color: C.textDark,
      marginBottom: 8,
      textAlign: "center" as const,
    },
    p: {
      fontSize: 14,
      color: C.textMuted,
      lineHeight: 1.6,
      textAlign: "center" as const,
    },
    label: {
      fontSize: 13,
      fontWeight: 500,
      color: C.textDark,
      marginBottom: 8,
      display: "block" as const,
    },
    btn: {
      width: "100%",
      height: 52,
      borderRadius: 14,
      background: C.purple,
      color: C.white,
      fontSize: 15,
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
    select: {
      flex: 1,
      height: 48,
      borderRadius: 10,
      border: `1px solid ${C.lightBorder}`,
      background: C.lightCard,
      color: C.textDark,
      padding: "0 12px",
      fontSize: 14,
    },
    energyBtn: (active: boolean) => ({
      flex: 1,
      height: 72,
      borderRadius: 12,
      border: `2px solid ${active ? C.purple : C.lightBorder}`,
      background: active ? C.purpleSoft : "transparent",
      color: active ? C.purple : C.textDark,
      fontSize: 14,
      fontWeight: active ? 600 : 500,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* ─── PAGE 1: Person 1 Data ─── */}
        {page === "person1" && (
          <>
            <div style={styles.icon}>👤</div>
            <h1 style={styles.h1}>{ls("Your Details")}</h1>
            <p style={{ ...styles.p, marginBottom: 24 }}>
              {ls("Enter your birth details for the compatibility reading")}
            </p>

            {/* Energy Signature */}
            <div style={{ width: "100%", marginBottom: 20 }}>
              <label style={styles.label}>{ls("Energy Signature")}</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                }}
              >
                {ENERGY_OPTIONS.map((o) => (
                  <button
                    key={o}
                    onClick={() => setPerson1Energy(o)}
                    style={styles.energyBtn(person1Energy === o)}
                  >
                    <span style={{ fontSize: 22 }}>
                      {o === "Soft" ? "🌸" : o === "Balanced" ? "⚖️" : "🌟"}
                    </span>
                    {ls(o)}
                  </button>
                ))}
              </div>
            </div>

            {/* Date of Birth */}
            <div style={{ width: "100%", marginBottom: 20 }}>
              <label style={styles.label}>{ls("Date of Birth")}</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: 10,
                }}
              >
                <select
                  value={person1DobY}
                  onChange={(e) => setPerson1DobY(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Year")}</option>
                  {YEARS.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person1DobM}
                  onChange={(e) => setPerson1DobM(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Month")}</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person1DobD}
                  onChange={(e) => setPerson1DobD(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Day")}</option>
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Destiny Time (optional) */}
            <div style={{ width: "100%", marginBottom: 24 }}>
              <label style={styles.label}>{ls("Destiny Time (Optional)")}</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <select
                  value={person1TimeH}
                  onChange={(e) => setPerson1TimeH(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Hour")}</option>
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person1TimeMin}
                  onChange={(e) => setPerson1TimeMin(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Minute")}</option>
                  {Array.from({ length: 12 }, (_, i) => ({
                    value: String(i * 5).padStart(2, "0"),
                    label: String(i * 5).padStart(2, "0"),
                  })).map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              style={{
                ...styles.btn,
                ...(!person1Dob ? styles.btnDisabled : {}),
              }}
              onClick={() => setPage("person2")}
              disabled={!person1Dob}
            >
              {ls("Next: Enter Partner Details")}
            </button>
          </>
        )}

        {/* ─── PAGE 2: Person 2 Data ─── */}
        {page === "person2" && (
          <>
            <button
              onClick={() => setPage("person1")}
              style={{
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                color: C.textMuted,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginBottom: 16,
                fontSize: 14,
              }}
            >
              <ChevronLeft size={16} /> {ls("Back")}
            </button>

            <div style={styles.icon}>💑</div>
            <h1 style={styles.h1}>{ls("Partner Details")}</h1>
            <p style={{ ...styles.p, marginBottom: 24 }}>
              {ls("Enter your partner's birth details")}
            </p>

            {/* Energy Signature */}
            <div style={{ width: "100%", marginBottom: 20 }}>
              <label style={styles.label}>{ls("Partner's Energy Signature")}</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                }}
              >
                {ENERGY_OPTIONS.map((o) => (
                  <button
                    key={o}
                    onClick={() => setPerson2Energy(o)}
                    style={styles.energyBtn(person2Energy === o)}
                  >
                    <span style={{ fontSize: 22 }}>
                      {o === "Soft" ? "🌸" : o === "Balanced" ? "⚖️" : "🌟"}
                    </span>
                    {ls(o)}
                  </button>
                ))}
              </div>
            </div>

            {/* Date of Birth */}
            <div style={{ width: "100%", marginBottom: 20 }}>
              <label style={styles.label}>{ls("Partner's Date of Birth")}</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: 10,
                }}
              >
                <select
                  value={person2DobY}
                  onChange={(e) => setPerson2DobY(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Year")}</option>
                  {YEARS.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person2DobM}
                  onChange={(e) => setPerson2DobM(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Month")}</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person2DobD}
                  onChange={(e) => setPerson2DobD(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Day")}</option>
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Destiny Time (optional) */}
            <div style={{ width: "100%", marginBottom: 24 }}>
              <label style={styles.label}>
                {ls("Partner's Destiny Time (Optional)")}
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <select
                  value={person2TimeH}
                  onChange={(e) => setPerson2TimeH(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Hour")}</option>
                  {HOURS.map((h) => (
                    <option key={h.value} value={h.value}>
                      {h.label}
                    </option>
                  ))}
                </select>
                <select
                  value={person2TimeMin}
                  onChange={(e) => setPerson2TimeMin(e.target.value)}
                  style={styles.select}
                >
                  <option value="">{ls("Minute")}</option>
                  {Array.from({ length: 12 }, (_, i) => ({
                    value: String(i * 5).padStart(2, "0"),
                    label: String(i * 5).padStart(2, "0"),
                  })).map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              style={{
                ...styles.btn,
                ...(!person2Dob ? styles.btnDisabled : {}),
              }}
              onClick={handleSubmit}
              disabled={!person2Dob}
            >
              {ls("Check Compatibility")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GCCCompatibility3Box;
